import { useState, useEffect } from 'react';
import { createShareLink, getShareLinks, revokeShareLink } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Link2, Copy, Trash2, Plus, ExternalLink, Users, Lock } from 'lucide-react';
import { toast } from 'sonner';

export const ShareLinks = ({ tripId }) => {
  const [links, setLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ scope: 'timeline', pin: '', expires_days: 30 });

  const load = () => { getShareLinks(tripId).then(r => setLinks(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, [tripId]); // eslint-disable-line

  const handleCreate = async () => {
    await createShareLink(tripId, { ...form, pin: form.pin || null });
    setOpen(false); toast.success('Lien cree !'); load();
  };

  const handleCopy = (linkId) => {
    const url = `${window.location.origin}/shared/${linkId}`;
    navigator.clipboard.writeText(url);
    toast.success('Lien copie !');
  };

  const handleRevoke = async (linkId) => {
    await revokeShareLink(linkId); toast.success('Lien revoque'); load();
  };

  return (
    <div className="space-y-4" data-testid="share-links">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-[#1E293B]">Voyagez avec nous</h3>
          <p className="text-xs text-[#94A3B8]">Partagez votre voyage en lecture seule avec vos proches</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white text-xs h-9" data-testid="create-share-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Creer un lien
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau lien de partage</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm">PIN de protection (optionnel)</Label>
                <Input value={form.pin} onChange={e => setForm({...form, pin: e.target.value})} placeholder="1234" className="mt-1 h-11" data-testid="share-pin" />
              </div>
              <div>
                <Label className="text-sm">Expire dans (jours)</Label>
                <Input type="number" value={form.expires_days} onChange={e => setForm({...form, expires_days: parseInt(e.target.value) || 30})} className="mt-1 h-11" />
              </div>
              <Button onClick={handleCreate} className="w-full rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white" data-testid="save-share-btn">Creer le lien</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {links.length === 0 ? (
        <div className="widget-card p-10 text-center">
          <Users className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-sm text-[#94A3B8]">Aucun lien de partage. Creez-en un pour vos proches.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {links.map(link => (
            <div key={link.link_id} className="widget-card p-4 flex items-center gap-4" data-testid={`share-${link.link_id}`}>
              <div className="w-10 h-10 rounded-xl bg-[#EBF2FF] flex items-center justify-center">
                <Link2 className="w-5 h-5 text-[#3A86FF]" strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1E293B] truncate">/shared/{link.link_id}</p>
                <div className="flex items-center gap-2 text-[10px] text-[#94A3B8]">
                  <Badge className={`text-[9px] border-0 rounded-md ${link.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                    {link.is_active ? 'Actif' : 'Revoque'}
                  </Badge>
                  {link.pin && <span className="flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" /> PIN</span>}
                  <span>Expire: {new Date(link.expires_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => handleCopy(link.link_id)} className="h-8 w-8 p-0" data-testid={`copy-${link.link_id}`}>
                  <Copy className="w-4 h-4 text-[#3A86FF]" />
                </Button>
                {link.is_active && (
                  <Button variant="ghost" size="sm" onClick={() => handleRevoke(link.link_id)} className="h-8 w-8 p-0 text-red-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
